import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { alpha } from "@mui/material/styles";
import React, { useState } from "react";
import { NumericFormat } from "react-number-format";
import {
  Add,
  Close,
  DeleteOutline,
  InfoOutlined,
  ReceiptLongOutlined,
  ShieldOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { chiaveGaranzia, useFormAccordo } from "../ContextAccordo";

const OPZIONI = [
  {
    campo: "obbligatorio",
    label: "Obbligatorio",
    hint: "Il cliente non può deselezionarla",
  },
  { campo: "visibile", label: "Visibile", hint: "Mostrata nel preventivo" },
  {
    campo: "frazionamento",
    label: "Frazionamento",
    hint: "Il prezzo segue il frazionamento",
  },
  { campo: "incluso", label: "Incluso", hint: "Compresa nel premio" },
  {
    campo: "garanziaRatino",
    label: "Garanzia Ratino",
    hint: "Calcolata sul rateo",
  },
];

const garanziaVuota = {
  id: null,
  idUid: "",
  idGaranzia: null,
  isNew: 1,
  garanziaRatino: 0,
  nome: "",
  idCode: "",
  prezzo: "",
  descrizione: "",
  obbligatorio: 0,
  visibile: 0,
  frazionamento: 0,
  incluso: 0,
};

// Le righe { label, value } in modifica hanno una chiave locale per React
let contatoreRighe = 0;
const nuovaRiga = (riga = {}) => ({
  chiave: `riga-${++contatoreRighe}`,
  label: riga.label ?? "",
  value: riga.value ?? "",
});

const righeDa = (sezione) =>
  (Array.isArray(sezione?.dataset) ? sezione.dataset : []).map(nuovaRiga);

// Una sezione senza voci viene salvata come {}
const sezioneDa = (sezioneOriginale, righe) => {
  if (!righe.length) return {};
  const dataset = righe.map((r) => ({
    label: r.label.trim(),
    value: String(r.value).trim(),
  }));
  const resto = { ...(sezioneOriginale || {}) };
  delete resto.dataset;
  return { ...resto, dataset };
};

const vuoto = (valore) => String(valore ?? "").trim() === "";

// Una voce aggiunta deve avere sia label che valore
const rigaIncompleta = (r) => vuoto(r.label) || vuoto(r.value);

const Sezione = ({ icon, titolo, sottotitolo, azione, children }) => (
  <Box component="section">
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      mb={3}
    >
      <Box display="flex" alignItems="center" gap={2} minWidth={0}>
        <Box
          sx={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: 1,
            display: "grid",
            placeItems: "center",
            color: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          }}
        >
          {icon}
        </Box>
        <Box minWidth={0}>
          <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
            {titolo}
          </Typography>
          {sottotitolo && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
            >
              {sottotitolo}
            </Typography>
          )}
        </Box>
      </Box>
      {azione && <Box flexShrink={0}>{azione}</Box>}
    </Box>
    {children}
  </Box>
);

// Da tablet in su la modale ha altezza fissa: Dati garanzia e tab restano
// fermi e scorre solo il contenuto del tab. Su telefono scorre tutta la modale
const flessibile = (t) => ({
  [t.breakpoints.up("sm")]: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
});

const scorrevole = (t) => ({
  [t.breakpoints.up("sm")]: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    // Spazio per la label dei campi, altrimenti tagliata dal bordo superiore
    pt: 1,
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
});

const PannelloDataset = ({ sottotitolo, azione, children }) => (
  <Box sx={flessibile}>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      mb={3}
      minHeight={36}
    >
      <Typography variant="caption" color="text.secondary">
        {sottotitolo}
      </Typography>
      {azione && <Box flexShrink={0}>{azione}</Box>}
    </Box>
    <Box sx={scorrevole}>{children}</Box>
  </Box>
);

const RigheDataset = ({
  righe,
  setRighe,
  etichetta,
  placeholderValue,
  mostraErrori,
}) => {
  const aggiorna = (chiave, campo, valore) =>
    setRighe(
      righe.map((r) => (r.chiave === chiave ? { ...r, [campo]: valore } : r)),
    );

  if (!righe.length) {
    return (
      <Box
        onClick={() => setRighe([nuovaRiga()])}
        sx={{
          border: 1,
          borderStyle: "dashed",
          borderColor: "divider",
          borderRadius: 1,
          py: 4,
          textAlign: "center",
          cursor: "pointer",
          color: "text.secondary",
          transition: "all .15s",
          "&:hover": { borderColor: "primary.main", color: "primary.main" },
        }}
      >
        <Typography variant="body2">
          Nessuna voce in {etichetta}. Clicca per aggiungerne una.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {righe.map((r, i) => (
        <Box key={r.chiave} display="flex" alignItems="flex-start" gap={2}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ width: 20, pt: 2.5, textAlign: "right", flexShrink: 0 }}
          >
            {i + 1}
          </Typography>
          <TextField
            size="small"
            label="Label"
            placeholder="Es. Condizioni"
            value={r.label}
            onChange={(e) => aggiorna(r.chiave, "label", e.target.value)}
            required
            error={mostraErrori && vuoto(r.label)}
            helperText={
              mostraErrori && vuoto(r.label) ? "Inserisci la label" : ""
            }
            sx={{ width: { xs: "40%", sm: 180 }, flexShrink: 0 }}
          />
          <TextField
            size="small"
            label="Valore"
            placeholder={placeholderValue}
            value={r.value}
            onChange={(e) => aggiorna(r.chiave, "value", e.target.value)}
            required
            error={mostraErrori && vuoto(r.value)}
            helperText={
              mostraErrori && vuoto(r.value) ? "Inserisci il valore" : ""
            }
            multiline
            maxRows={4}
            fullWidth
          />
          <Tooltip title="Rimuovi voce">
            <IconButton
              size="small"
              onClick={() =>
                setRighe(righe.filter((x) => x.chiave !== r.chiave))
              }
              sx={{ mt: 1, "&:hover": { color: "error.main" } }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Stack>
  );
};

// Aggiunge una nuova garanzia o, se riceve `garanzia`, ne modifica una esistente
const GaranzieModale = ({ open, setOpen, garanzia: daModificare = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { dispatch } = useFormAccordo();
  const modifica = daModificare !== null;

  const [garanzia, setGaranzia] = useState(() => ({
    ...garanziaVuota,
    ...(daModificare || {}),
  }));
  const [costi, setCosti] = useState(() =>
    righeDa(daModificare?.dataCollector?.riepilogoCosti),
  );
  const [informativa, setInformativa] = useState(() =>
    righeDa(daModificare?.dataCollector?.informativa),
  );
  const [tentato, setTentato] = useState(false);
  const [tab, setTab] = useState("opzioni");

  const set = (campo, valore) => setGaranzia({ ...garanzia, [campo]: valore });

  const nomeVuoto = vuoto(garanzia.nome);
  const idCodeVuoto = vuoto(garanzia.idCode);
  const prezzoVuoto = vuoto(garanzia.prezzo);
  const datiNonValidi = nomeVuoto || idCodeVuoto || prezzoVuoto;
  const costiNonValidi = costi.some(rigaIncompleta);
  const informativaNonValida = informativa.some(rigaIncompleta);
  const righeNonValide = costiNonValidi || informativaNonValida;

  const TABS = [
    {
      valore: "opzioni",
      label: "Opzioni",
      icon: <TuneOutlined fontSize="small" />,
    },
    {
      valore: "costi",
      label: "Riepilogo costi",
      icon: <ReceiptLongOutlined fontSize="small" />,
      conteggio: costi.length,
      errore: tentato && costiNonValidi,
    },
    {
      valore: "informativa",
      label: "Informativa",
      icon: <InfoOutlined fontSize="small" />,
      conteggio: informativa.length,
      errore: tentato && informativaNonValida,
    },
  ];

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    setTentato(true);
    // Porta in primo piano il tab con le righe da correggere
    if (righeNonValide) {
      setTab(costiNonValidi ? "costi" : "informativa");
    }
    if (datiNonValidi || righeNonValide) return;

    const dataCollector = {
      ...(garanzia.dataCollector || {}),
      riepilogoCosti: sezioneDa(garanzia.dataCollector?.riepilogoCosti, costi),
      informativa: sezioneDa(garanzia.dataCollector?.informativa, informativa),
    };
    const payload = {
      ...garanzia,
      nome: garanzia.nome.trim(),
      idCode: String(garanzia.idCode).trim(),
      descrizione: (garanzia.descrizione || "").trim(),
      dataCollector,
    };

    if (modifica) {
      dispatch({
        type: "set_garanzie",
        id: chiaveGaranzia(daModificare),
        payload,
      });
    } else {
      dispatch({
        type: "set_new_garanzia",
        payload: {
          garanzia: {
            ...payload,
            // idLocale serve solo come chiave della riga finche' l'accordo non
            // viene salvato
            idLocale: `nuova-${Date.now()}`,
          },
        },
      });
    }
    handleClose();
  };

  // Su telefono solo l'icona, allineata al bordo destro
  const bottoneAggiungi = (setRighe, righe) => {
    const aggiungi = () => setRighe([...righe, nuovaRiga()]);
    return isMobile ? (
      <Tooltip title="Aggiungi voce">
        <IconButton color="primary" edge="end" onClick={aggiungi}>
          <Add />
        </IconButton>
      </Tooltip>
    ) : (
      <Button size="small" startIcon={<Add />} onClick={aggiungi}>
        Aggiungi voce
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          // Altezza fissa: cambiando tab la modale non si ridimensiona, il
          // contenuto in eccesso scorre dentro DialogContent
          height: "min(760px, calc(100% - 64px))",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1B2635" : "#F7F9FC",
        },
      }}
    >
      <DialogTitle sx={{ pr: 12 }}>
        <Typography variant="h5" component="span" fontWeight={600}>
          {modifica ? "Modifica garanzia" : "Nuova garanzia"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {modifica
            ? "Modifica i dati della garanzia. Il nome non può essere modificato."
            : "Definisci i dati, le opzioni e i contenuti mostrati al cliente."}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
          aria-label="Chiudi"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          [theme.breakpoints.up("sm")]: {
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Stack
          spacing={6}
          divider={<Divider flexItem />}
          py={2}
          sx={flessibile}
        >
          <Sezione
            icon={<ShieldOutlined fontSize="small" />}
            titolo="Dati garanzia"
          >
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "2fr 1fr 1fr" }}
              gap={3}
            >
              <TextField
                size="small"
                label="Nome"
                required
                autoFocus={!modifica}
                value={garanzia.nome}
                onChange={(e) => set("nome", e.target.value)}
                InputProps={{ readOnly: modifica }}
                error={tentato && nomeVuoto}
                helperText={
                  tentato && nomeVuoto ? "Il nome è obbligatorio" : ""
                }
              />
              <TextField
                size="small"
                label="ID Code"
                required
                value={garanzia.idCode ?? ""}
                onChange={(e) => set("idCode", e.target.value)}
                error={tentato && idCodeVuoto}
                helperText={
                  tentato && idCodeVuoto ? "L'ID Code è obbligatorio" : ""
                }
              />
              <NumericFormat
                customInput={TextField}
                size="small"
                label="Prezzo"
                required
                error={tentato && prezzoVuoto}
                helperText={
                  tentato && prezzoVuoto ? "Il prezzo è obbligatorio" : ""
                }
                prefix="€"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                valueIsNumericString
                value={garanzia.prezzo}
                onValueChange={(e) => set("prezzo", e.value)}
              />
              <TextField
                size="small"
                label="Descrizione"
                placeholder="Es. Furto totale e parziale. Componente obbligatoria…"
                multiline
                minRows={3}
                value={garanzia.descrizione ?? ""}
                onChange={(e) => set("descrizione", e.target.value)}
                sx={{ gridColumn: "1 / -1" }}
              />
            </Box>
          </Sezione>

          <Box sx={flessibile}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 4,
                flexShrink: 0,
              }}
            >
              {TABS.map(({ valore, label, icon, conteggio, errore }) => (
                <Tab
                  key={valore}
                  value={valore}
                  icon={icon}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    textTransform: "none",
                    ...(errore && { color: "error.main" }),
                  }}
                  label={conteggio ? `${label} (${conteggio})` : label}
                />
              ))}
            </Tabs>

            {tab === "opzioni" && (
              <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                alignContent="start"
                gap={2}
                sx={scorrevole}
              >
                {OPZIONI.map(({ campo, label, hint }) => {
                  const attivo = garanzia[campo] === 1;
                  return (
                    <Box
                      key={campo}
                      component="label"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      sx={{
                        px: 3,
                        py: 2,
                        borderRadius: 1,
                        border: 1,
                        cursor: "pointer",
                        borderColor: attivo ? "primary.main" : "divider",
                        bgcolor: (t) =>
                          attivo
                            ? alpha(t.palette.primary.main, 0.06)
                            : "background.paper",
                        transition: "all .15s",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hint}
                        </Typography>
                      </Box>
                      <Switch
                        checked={attivo}
                        onChange={(e) => set(campo, e.target.checked ? 1 : 0)}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            {tab === "costi" && (
              <PannelloDataset
                sottotitolo="Voci di costo della garanzia"
                azione={costi.length > 0 && bottoneAggiungi(setCosti, costi)}
              >
                <RigheDataset
                  righe={costi}
                  setRighe={setCosti}
                  etichetta="riepilogo costi"
                  placeholderValue="Es. 120,00 €"
                  mostraErrori={tentato}
                />
              </PannelloDataset>
            )}

            {tab === "informativa" && (
              <PannelloDataset
                sottotitolo="Condizioni e note per il cliente"
                azione={
                  informativa.length > 0 &&
                  bottoneAggiungi(setInformativa, informativa)
                }
              >
                <RigheDataset
                  righe={informativa}
                  setRighe={setInformativa}
                  etichetta="informativa"
                  placeholderValue="Es. Furto totale: franchigia 5.000 euro…"
                  mostraErrori={tentato}
                />
              </PannelloDataset>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={handleClose}>Annulla</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={tentato && (datiNonValidi || righeNonValide)}
        >
          {modifica ? "Salva modifiche" : "Aggiungi garanzia"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GaranzieModale;
